import { formatEuros } from '@imp/shared/format';
import { suiviFacturation } from '@imp/shared/suivi-facturation';

import { totalOrigineAcompte } from '@/lib/acompte-origine';
import { supabase } from '@/lib/supabase';
import { listColonnesSuivi } from '@/modules/chantiers/suivi-api';
import { type Contact, contactDisplayName, listContacts, ORG_ID } from '@/modules/clients/api';

export type Priorite = 'haute' | 'moyenne' | 'basse';

export interface Alerte {
  id: string;
  type: string;
  priorite: Priorite;
  titre: string;
  description: string;
  entite: 'devis' | 'facture' | 'chantier';
  entiteId: string;
  lien: string;
  client: string;
  montant?: number;
  jours?: number;
}

export interface AlertesResult {
  alertes: Alerte[];
  counts: { haute: number; moyenne: number; basse: number; total: number };
}

// Métadonnées d'affichage par priorité (couleurs cohérentes page + tableau de bord).
export const PRIORITE_META: Record<
  Priorite,
  { label: string; dot: string; badge: string; border: string; text: string }
> = {
  haute: {
    label: 'Haute',
    dot: 'bg-prio-high',
    badge: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
    border: 'border-l-prio-high',
    text: 'text-prio-high',
  },
  moyenne: {
    label: 'Moyenne',
    dot: 'bg-prio-mid',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
    border: 'border-l-prio-mid',
    text: 'text-prio-mid',
  },
  basse: {
    label: 'Basse',
    dot: 'bg-prio-low',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
    border: 'border-l-prio-low',
    text: 'text-prio-low',
  },
};

interface DevisRow {
  id: string;
  numero: string;
  statut: string;
  objet: string;
  date_expiration: string;
  envoye_at: string | null;
  montant_ht: string;
  montant_ttc: string;
  contact_id: string;
  chantier_id: string | null;
  metadata: { derniere_relance_at?: string } | null;
}
interface FactureRow {
  id: string;
  numero: string;
  statut: string;
  objet: string;
  date_echeance: string;
  montant_ht: string;
  montant_ttc: string;
  montant_paye: string;
  contact_id: string;
  devis_id: string | null;
  chantier_id: string | null;
  kind: string; // acompte | situation | finale | avoir | standard
  /** Pour un avoir : la facture qu'il annule. */
  facture_liee_id: string | null;
  metadata: unknown;
}

// Nombre de jours écoulés depuis `isoDate` (positif = passé).
function daysSince(isoDate: string, ref: number): number {
  const iso = isoDate.length === 10 ? `${isoDate}T00:00:00` : isoDate;
  return Math.floor((ref - new Date(iso).getTime()) / 86400000);
}

// Priorité d'une alerte de préparation selon le début prévu du chantier.
// Horizon 60 j : ≤ 15 j (ou déjà commencé) = urgence (haute) · ≤ 30 j = moyen ·
// 31–60 j = bas · > 60 j = null (trop tôt, pas encore d'alerte).
// Sans date de début : on garde l'alerte en « moyenne » (échéance inconnue).
function prioPreparation(datePrevueDebut: string | null, ref: number): Priorite | null {
  if (!datePrevueDebut) return 'moyenne';
  const joursAvant = -daysSince(datePrevueDebut, ref); // > 0 = à venir, ≤ 0 = déjà commencé
  if (joursAvant > 60) return null;
  if (joursAvant <= 15) return 'haute';
  if (joursAvant <= 30) return 'moyenne';
  return 'basse';
}

function formatJour(iso: string): string {
  const d = iso.length === 10 ? `${iso}T00:00:00` : iso;
  return new Date(d).toLocaleDateString('fr-FR');
}

const ORDER: Record<Priorite, number> = { haute: 0, moyenne: 1, basse: 2 };

export async function computeAlertes(): Promise<AlertesResult> {
  const now = Date.now();
  const [devisR, facturesR, contactsR, chantiersR, evChantiersR, masqueesR, colonnes] =
    await Promise.all([
    supabase
      .from('devis')
      .select(
        'id,numero,statut,objet,date_expiration,envoye_at,montant_ht,montant_ttc,contact_id,chantier_id,metadata',
      ),
    supabase
      .from('factures')
      .select(
        'id,numero,statut,objet,date_echeance,montant_ht,montant_ttc,montant_paye,contact_id,devis_id,chantier_id,kind,facture_liee_id,metadata',
      ),
    // Par tranches, comme partout : au-delà de 1 000 fiches, une lecture directe
    // serait tronquée sans le dire, et les alertes nommeraient « client ».
    listContacts(),
    supabase
      .from('chantiers')
      .select('id,numero,libelle,statut,contact_id,metadata,date_prevue_debut,montant_total_ht'),
    supabase
      .from('evenements')
      .select('chantier_id,fin_at')
      .not('chantier_id', 'is', null)
      // Une intervention annulée ne place plus le chantier.
      .neq('statut', 'annule'),
    // Alertes déjà traitées et encore masquées : on ne les fait pas remonter.
    supabase
      .from('alertes_traitees')
      .select('alerte_cle')
      .is('deleted_at', null)
      .gt('masquer_jusqu_a', new Date().toISOString()),
    // Colonnes du tableau de suivi : ce sont elles qui décident des étapes à
    // réclamer, et de la phrase employée. Repli sur les cinq d'origine.
    listColonnesSuivi(),
  ]);
  // Chaque lecture vérifie son erreur. Avant le 06/10/2026, seules les deux
  // premières le faisaient : une lecture des chantiers en échec donnait zéro
  // chantier à planifier, une lecture des interventions en échec faisait passer
  // TOUS les chantiers pour non planifiés — et rien ne le signalait. Une alerte
  // absente se croit ; une erreur se voit.
  const echec = (quoi: string, r: { error: { message: string } | null }): void => {
    if (r.error) throw new Error(`Impossible de lire ${quoi} : ${r.error.message}`);
  };
  echec('les devis', devisR);
  echec('les factures', facturesR);
  echec('les chantiers', chantiersR);
  echec('les interventions planifiées', evChantiersR);
  echec('les alertes déjà traitées', masqueesR);

  const devis = devisR.data as DevisRow[];
  const factures = facturesR.data as FactureRow[];
  const contacts: Contact[] = contactsR;
  const contactParId = new Map(contacts.map((c) => [c.id, c]));
  const nameOf = (cid: string): string => {
    const c = contactParId.get(cid);
    return c ? contactDisplayName(c) : 'client';
  };
  const facturedDevis = new Set(factures.map((f) => f.devis_id).filter(Boolean));

  const alertes: Alerte[] = [];

  for (const d of devis) {
    const client = nameOf(d.contact_id);
    const ttc = Number(d.montant_ttc);
    const expJ = daysSince(d.date_expiration, now); // >0 = expiré

    if (d.statut === 'a_envoyer') {
      alertes.push({
        id: `dev-env-${d.id}`,
        type: 'devis_a_envoyer',
        priorite: 'moyenne',
        titre: `Devis ${d.numero} à envoyer`,
        description: `À transmettre à ${client}.`,
        entite: 'devis',
        entiteId: d.id,
        lien: `/devis/${d.id}`,
        client,
        montant: ttc,
      });
    }

    if (d.statut === 'envoye' || d.statut === 'consulte') {
      if (expJ > 0) {
        alertes.push({
          id: `dev-exp-${d.id}`,
          type: 'devis_expire',
          priorite: 'haute',
          titre: `Devis ${d.numero} expiré`,
          description: `Sans réponse, expiré depuis ${expJ} j — ${client}.`,
          entite: 'devis',
          entiteId: d.id,
          lien: `/devis/${d.id}`,
          client,
          montant: ttc,
          jours: expJ,
        });
      } else {
        const sentJ = d.envoye_at ? daysSince(d.envoye_at.slice(0, 10), now) : null;
        const relJ = d.metadata?.derniere_relance_at
          ? daysSince(d.metadata.derniere_relance_at.slice(0, 10), now)
          : null;
        if (sentJ !== null && sentJ >= 7 && (relJ === null || relJ >= 7)) {
          alertes.push({
            id: `dev-rel-${d.id}`,
            type: 'devis_sans_reponse',
            priorite: sentJ >= 14 ? 'haute' : 'moyenne',
            titre: `Relancer ${client}`,
            description: `Devis ${d.numero} sans réponse depuis ${sentJ} j.`,
            entite: 'devis',
            entiteId: d.id,
            lien: `/devis/${d.id}`,
            client,
            montant: ttc,
            jours: sentJ,
          });
        }
        if (expJ >= -7) {
          alertes.push({
            id: `dev-exps-${d.id}`,
            type: 'devis_expire_bientot',
            priorite: 'moyenne',
            titre: `Devis ${d.numero} expire bientôt`,
            description: `Expire dans ${-expJ} j — ${client}.`,
            entite: 'devis',
            entiteId: d.id,
            lien: `/devis/${d.id}`,
            client,
            montant: ttc,
          });
        }
      }
    }

    if (d.statut === 'signe' && !facturedDevis.has(d.id)) {
      alertes.push({
        id: `dev-conv-${d.id}`,
        type: 'devis_a_convertir',
        priorite: 'moyenne',
        titre: `Convertir le devis ${d.numero}`,
        description: `Accepté par ${client} — à facturer (${formatEuros(ttc)} TTC).`,
        entite: 'devis',
        entiteId: d.id,
        lien: `/devis/${d.id}`,
        client,
        montant: ttc,
      });
    }
  }

  for (const f of factures) {
    if (f.kind === 'avoir') continue; // un avoir ne s'encaisse pas : il annule une facture
    if (f.statut === 'brouillon') {
      // Un brouillon n'a ni numéro ni échéance qui vaille : il n'entre dans aucune
      // des alertes d'encaissement ci-dessous. Mais tant qu'il reste brouillon,
      // le client ne le reçoit pas et l'argent n'est pas réclamé — c'est une
      // action à faire, et la première qu'on oublie.
      const clientBrouillon = nameOf(f.contact_id);
      alertes.push({
        id: `fac-brou-${f.id}`,
        type: 'facture_brouillon',
        priorite: 'moyenne',
        titre: `Facture en brouillon à émettre — ${clientBrouillon}`,
        description: `Pas encore émise : ${clientBrouillon} ne la recevra pas tant qu'elle reste en brouillon.`,
        entite: 'facture',
        entiteId: f.id,
        lien: `/factures/${f.id}`,
        client: clientBrouillon,
        montant: Number(f.montant_ttc),
      });
      continue;
    }
    if (f.statut === 'payee' || f.statut === 'annulee_avoir') continue;
    const client = nameOf(f.contact_id);
    const reste = Number(f.montant_ttc) - Number(f.montant_paye);
    const echJ = daysSince(f.date_echeance, now); // >0 = en retard

    if (echJ > 0) {
      alertes.push({
        id: `fac-ret-${f.id}`,
        type: 'facture_en_retard',
        priorite: 'haute',
        titre: `Facture ${f.numero} en retard`,
        description: `En retard de ${echJ} j — relancer ${client}.`,
        entite: 'facture',
        entiteId: f.id,
        lien: `/factures/${f.id}`,
        client,
        montant: reste,
        jours: echJ,
      });
    } else if (f.statut === 'emise') {
      alertes.push({
        id: `fac-env-${f.id}`,
        type: 'facture_a_envoyer',
        priorite: 'moyenne',
        titre: `Envoyer la facture ${f.numero}`,
        description: `Émise, à transmettre à ${client}.`,
        entite: 'facture',
        entiteId: f.id,
        lien: `/factures/${f.id}`,
        client,
        montant: reste,
      });
    } else if (echJ >= -7) {
      alertes.push({
        id: `fac-ech-${f.id}`,
        type: 'facture_echeance',
        priorite: 'moyenne',
        titre: `Facture ${f.numero} à échéance`,
        description: `Échéance dans ${-echJ} j — ${client}.`,
        entite: 'facture',
        entiteId: f.id,
        lien: `/factures/${f.id}`,
        client,
        montant: reste,
      });
    }

    if (f.statut === 'partiellement_payee') {
      alertes.push({
        id: `fac-solde-${f.id}`,
        type: 'facture_solde',
        priorite: 'basse',
        titre: `Solde à encaisser — ${f.numero}`,
        description: `Reste ${formatEuros(reste)} à percevoir de ${client}.`,
        entite: 'facture',
        entiteId: f.id,
        lien: `/factures/${f.id}`,
        client,
        montant: reste,
      });
    }
  }

  // Chantiers engagés (planifiés / en cours) sans intervention dans l'agenda.
  const chantiers = (chantiersR.data ?? []) as {
    id: string;
    numero: string;
    libelle: string;
    statut: string;
    contact_id: string | null;
    metadata: {
      nacelle_requise?: boolean;
      nacelle_magasin?: string;
      nacelle_commandee?: boolean;
      prep?: Record<string, string>;
    } | null;
    date_prevue_debut: string | null;
    montant_total_ht: number | string;
  }[];
  const planifies = new Set(
    (evChantiersR.data ?? [])
      .map((e) => (e as { chantier_id: string | null }).chantier_id)
      .filter(Boolean),
  );
  for (const ch of chantiers) {
    const client = ch.contact_id ? nameOf(ch.contact_id) : 'sans client';
    const m = ch.metadata;
    const enCours =
      ch.statut === 'prospect' || ch.statut === 'planifie' || ch.statut === 'en_cours';

    // Nacelle à commander (si renseignée comme nécessaire et pas encore commandée).
    if (m?.nacelle_requise && !m.nacelle_commandee && enCours) {
      const prio = prioPreparation(ch.date_prevue_debut, now);
      if (prio) {
        const mag = m.nacelle_magasin ? ` chez ${m.nacelle_magasin}` : '';
        alertes.push({
          id: `chan-nac-${ch.id}`,
          type: 'nacelle_a_commander',
          priorite: prio,
          titre: `Commander la nacelle — ${ch.libelle}`,
          description: `À commander${mag}${ch.contact_id ? ` pour ${client}` : ''}${ch.date_prevue_debut ? ` — démarrage le ${formatJour(ch.date_prevue_debut)}` : ''}.`,
          entite: 'chantier',
          entiteId: ch.id,
          lien: `/chantiers/${ch.id}`,
          client,
        });
      }
    }

    // Étapes de préparation marquées « à faire ».
    if (m?.prep && enCours) {
      const prio = prioPreparation(ch.date_prevue_debut, now);
      if (prio) {
        for (const item of colonnes) {
          if (!item.actif) continue;
          if (m.prep[item.cle] === 'todo') {
            alertes.push({
              id: `chan-prep-${item.cle}-${ch.id}`,
              type: `prep_${item.cle}`,
              priorite: prio,
              titre: `${item.alerte ?? `${item.label} à faire`} — ${ch.libelle}`,
              description: `${ch.date_prevue_debut ? `Démarrage le ${formatJour(ch.date_prevue_debut)}.` : 'Début à planifier.'}${ch.contact_id ? ` Client : ${client}.` : ''}`,
              entite: 'chantier',
              entiteId: ch.id,
              lien: `/chantiers/${ch.id}`,
              client,
            });
          }
        }
      }
    }

    // Chantier engagé sans intervention planifiée dans l'agenda.
    if ((ch.statut === 'planifie' || ch.statut === 'en_cours') && !planifies.has(ch.id)) {
      alertes.push({
        id: `chan-plan-${ch.id}`,
        type: 'chantier_non_planifie',
        priorite: 'moyenne',
        titre: `À planifier — ${ch.libelle}`,
        description: `Aucune intervention dans l'agenda${ch.contact_id ? ` · ${client}` : ''}.`,
        entite: 'chantier',
        entiteId: ch.id,
        lien: `/chantiers/${ch.id}`,
        client,
      });
    }
  }

  // Facturation à réaliser : chantier placé dont l'intervention est passée.
  //
  // Dérivé de LA MÊME fonction que l'onglet « À facturer » du Pilotage : deux
  // calculs séparés finiraient par se contredire, et l'alerte perdrait toute
  // valeur le jour où elle ne correspondrait plus au tableau.
  const interventions = (evChantiersR.data ?? []) as {
    chantier_id: string | null;
    fin_at: string;
  }[];
  const aFacturer = suiviFacturation(
    chantiers.map((ch) => ({
      id: ch.id,
      numero: ch.numero,
      libelle: ch.libelle,
      statut: ch.statut,
      contactId: ch.contact_id,
      montantTotalHt: Number(ch.montant_total_ht),
    })),
    interventions.map((e) => ({ chantierId: e.chantier_id, finAt: e.fin_at })),
    factures.map((f) => ({
      id: f.id,
      chantierId: f.chantier_id,
      contactId: f.contact_id,
      numero: f.numero,
      kind: f.kind,
      statut: f.statut,
      factureLieeId: f.facture_liee_id,
      montantHt: Number(f.montant_ht),
      montantTtc: Number(f.montant_ttc),
      montantPaye: Number(f.montant_paye),
      montantOrigineHt: totalOrigineAcompte(f.metadata),
    })),
    devis.map((d) => ({
      id: d.id,
      chantierId: d.chantier_id,
      contactId: d.contact_id,
      numero: d.numero,
      statut: d.statut,
      montantHt: Number(d.montant_ht),
      montantTtc: Number(d.montant_ttc),
    })),
    now,
  );
  for (const l of aFacturer) {
    // Une intervention passée a forcément une date : le garde rassure le
    // compilateur sans rien changer au comportement.
    if (!l.interventionPassee || !l.finIntervention) continue;
    // Le chantier reste dans le suivi tant qu'il n'est pas encaissé, mais
    // « à facturer » deviendrait faux une fois tout facturé : ce qui manque
    // alors, c'est le règlement, et les alertes de facture impayée le disent
    // déjà. Deux alertes pour un même euro n'aident personne.
    if (l.motif === 'attente_reglement') continue;
    const client = l.contactId ? nameOf(l.contactId) : 'sans client';
    alertes.push({
      id: `chan-fact-${l.chantierId}`,
      type: 'facturation_a_realiser',
      // Plus l'intervention est loin, plus l'argent dort.
      priorite: l.joursDepuis !== null && l.joursDepuis >= 7 ? 'haute' : 'moyenne',
      titre: `Chantier terminé, à facturer — ${l.libelle}`,
      description: [
        `Intervention terminée le ${formatJour(l.finIntervention)}`,
        l.contactId ? ` · ${client}` : '',
        // Sous-traitance : les travaux se font chez l'un, la facture part chez
        // l'autre. Sans cette mention, l'alerte désigne le mauvais destinataire.
        l.contactFactureId !== null ? ` · à facturer à ${nameOf(l.contactFactureId)}` : '',
        // On ne l'affirme que sur un chantier dont le CRM porte les documents.
        // Ailleurs (facturé dans Extrabat), on se tait plutôt que d'inventer.
        l.resteAFacturerHt === null
          ? ''
          : ` · reste ${formatEuros(l.resteAFacturerHt)} HT à facturer`,
        l.acompteBrouillon ? ' (acompte en brouillon)' : '',
        '.',
      ].join(''),
      entite: 'chantier',
      entiteId: l.chantierId,
      lien: `/chantiers/${l.chantierId}`,
      client,
      // Le montant n'accompagne l'alerte que si le CRM sait ce qu'il reste.
      ...(l.resteAFacturerHt !== null && l.resteAFacturerHt > 0
        ? { montant: l.resteAFacturerHt }
        : {}),
      ...(l.joursDepuis !== null ? { jours: l.joursDepuis } : {}),
    });
  }

  // Retire les alertes traitées récemment (masquées jusqu'à leur date d'expiration).
  const masquees = new Set(
    (masqueesR.data ?? []).map((m) => (m as { alerte_cle: string }).alerte_cle),
  );
  const visibles = alertes.filter((a) => !masquees.has(a.id));

  visibles.sort((a, b) => ORDER[a.priorite] - ORDER[b.priorite] || (b.jours ?? 0) - (a.jours ?? 0));
  const counts = {
    haute: visibles.filter((a) => a.priorite === 'haute').length,
    moyenne: visibles.filter((a) => a.priorite === 'moyenne').length,
    basse: visibles.filter((a) => a.priorite === 'basse').length,
    total: visibles.length,
  };
  return { alertes: visibles, counts };
}

// ─── Historique des alertes traitées ─────────────────────────────────────────
// Les alertes n'existent pas en base (elles sont calculées) : on enregistre donc
// un INSTANTANÉ au moment du traitement.

export type AlerteAction = 'relance' | 'traite';

export const ACTION_LABELS: Record<string, string> = {
  relance: 'Relancé',
  traite: 'Traité',
};

// Durée pendant laquelle une alerte traitée cesse de remonter.
export const MASQUAGE_JOURS = 7;

export interface AlerteTraitee {
  id: string;
  alerte_cle: string;
  alerte_type: string;
  priorite: Priorite | null;
  titre: string;
  description: string | null;
  entite_type: string | null;
  entite_id: string | null;
  client: string | null;
  montant: number | null;
  jours: number | null;
  action: string;
  commentaire: string | null;
  traite_par_nom: string | null;
  traite_at: string;
  masquer_jusqu_a: string | null;
}

export async function enregistrerAlerteTraitee(
  a: Alerte,
  action: AlerteAction,
  auteur?: { id?: string | null; nom?: string | null },
): Promise<void> {
  const masquerJusquA = new Date(Date.now() + MASQUAGE_JOURS * 86400000).toISOString();
  const { error } = await supabase.from('alertes_traitees').insert({
    organization_id: ORG_ID,
    alerte_cle: a.id,
    alerte_type: a.type,
    priorite: a.priorite,
    titre: a.titre,
    description: a.description,
    entite_type: a.entite,
    entite_id: a.entiteId,
    client: a.client,
    montant: a.montant ?? null,
    jours: a.jours ?? null,
    action,
    traite_par_id: auteur?.id ?? null,
    traite_par_nom: auteur?.nom ?? null,
    masquer_jusqu_a: masquerJusquA,
  });
  if (error) throw new Error(error.message);
}

export async function listAlertesTraitees(limit = 500): Promise<AlerteTraitee[]> {
  const { data, error } = await supabase
    .from('alertes_traitees')
    .select(
      'id, alerte_cle, alerte_type, priorite, titre, description, entite_type, entite_id, client, montant, jours, action, commentaire, traite_par_nom, traite_at, masquer_jusqu_a',
    )
    .is('deleted_at', null)
    .order('traite_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return data as AlerteTraitee[];
}

// Annule le masquage : l'alerte pourra de nouveau remonter (l'historique est conservé).
export async function reactiverAlerte(id: string): Promise<void> {
  const { error } = await supabase
    .from('alertes_traitees')
    .update({ masquer_jusqu_a: null })
    .eq('id', id);
  if (error) throw new Error(error.message);
}
